/**
 * RoiCumulativeChart — inline-SVG line chart (no Recharts dep) showing
 * cumulative cost over 5 years for Heat Pump vs Electric / Diesel / Gas
 * boilers. Mirrors the renderROIChart() routine in
 * `docs/calc-references/commercial-heat-pump.html` (W=700, H=320,
 * 6 evenly-spaced gridlines, HP stroke=3, others stroke=2).
 *
 * Colors match ROI_TECHS palette from the reference HTML.
 */
export interface RoiSeries {
  hp: number[];
  elec: number[];
  diesel: number[];
  gas: number[];
}

const COLORS = {
  hp: '#2E5D33',
  elec: '#856404',
  diesel: '#721c24',
  gas: '#1F3F66',
} as const;

const LABELS = {
  hp: 'Heat Pump',
  elec: 'Electric Boiler',
  diesel: 'Diesel Boiler',
  gas: 'Gas Boiler',
} as const;

export function RoiCumulativeChart({ data }: { data: RoiSeries }) {
  const techs = ['hp', 'elec', 'diesel', 'gas'] as const;
  const all = techs.flatMap((k) => data[k]);
  const maxVal = Math.max(...all, 1);
  const W = 700;
  const H = 320;
  const M = { top: 20, right: 130, bottom: 50, left: 80 };
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const xScale = (i: number) => M.left + (i / 5) * plotW;
  const yScale = (v: number) => M.top + plotH - (v / maxVal) * plotH;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto max-h-[380px]"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Horizontal gridlines + ₹ axis labels */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const v = (maxVal * i) / 5;
        const y = yScale(v);
        return (
          <g key={`grid-${i}`}>
            <line
              x1={M.left}
              y1={y}
              x2={W - M.right}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
            <text
              x={M.left - 8}
              y={y + 4}
              textAnchor="end"
              fontSize="10"
              fill="#6b7280"
            >
              ₹
              {v >= 1e5
                ? (v / 1e5).toFixed(1) + 'L'
                : (v / 1e3).toFixed(0) + 'k'}
            </text>
          </g>
        );
      })}

      {/* X-axis: Year 0..5 */}
      {[0, 1, 2, 3, 4, 5].map((y) => (
        <text
          key={`x-${y}`}
          x={xScale(y)}
          y={H - M.bottom + 18}
          textAnchor="middle"
          fontSize="10"
          fill="#6b7280"
        >
          Year {y}
        </text>
      ))}

      {/* Per-tech polylines + end-point markers + tech labels */}
      {techs.map((k) => {
        const pts = data[k].map((v, i) => `${xScale(i)},${yScale(v)}`).join(' ');
        const lastX = xScale(5);
        const lastY = yScale(data[k][5]);
        return (
          <g key={k}>
            <polyline
              points={pts}
              fill="none"
              stroke={COLORS[k]}
              strokeWidth={k === 'hp' ? 3 : 2}
            />
            <circle cx={lastX} cy={lastY} r={4} fill={COLORS[k]} />
            <text
              x={lastX + 8}
              y={lastY + 4}
              fontSize="11"
              fill={COLORS[k]}
              fontWeight={500}
            >
              {LABELS[k]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
